// Page stack geometry for PDF cards, after the Instagram carousel stack
// (lib/socialCard CAROUSEL_STACK): the current page in front, the next one
// peeking out behind it on the right, dots underneath. On the last page the
// stack flips and the previous page peeks out on the left.
//
// Unlike a carousel post, a PDF card doesn't resize itself to fit its pages:
// the stack fits inside whatever box the card has, at the pages' own ratio,
// so portrait reports and landscape decks both sit centred.

export interface StackBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface PdfStackLayout {
  front: StackBox;
  peekRight: StackBox;
  peekLeft: StackBox;
  /** The front page when the stack is flipped (last page). */
  frontFlipped: StackBox;
  /** Strip under the front page for the dots. */
  dotsTop: number;
}

export const PDF_STACK = {
  pad: 14,
  dotsHeight: 22,
  /** How much of the next page shows past the front one, as a fraction of the front's width. */
  reveal: 0.14,
  /** Peeking page, relative to the front one. */
  peekScale: 0.86,
} as const;

/** Letter / A4-ish: the guess until the first page has loaded. */
export const DEFAULT_PAGE_ASPECT = 1.3;

/**
 * Lay out the stack in a `width` x `height` px area for pages `aspect`
 * (height / width) tall. `multi` false gives a single centred page, no peek
 * and no dots.
 */
export function pdfStackLayout(width: number, height: number, aspect: number, multi: boolean): PdfStackLayout {
  const { pad, dotsHeight, reveal, peekScale } = PDF_STACK;
  const safeAspect = Number.isFinite(aspect) && aspect > 0 ? Math.min(4, Math.max(0.25, aspect)) : DEFAULT_PAGE_ASPECT;
  const r = multi ? reveal : 0;

  const availW = Math.max(0, width - pad * 2);
  const availH = Math.max(0, height - pad - (multi ? dotsHeight : pad));
  const frontW = Math.max(0, Math.min(availW / (1 + r), availH / safeAspect));
  const frontH = frontW * safeAspect;

  const groupW = frontW * (1 + r);
  const groupLeft = (width - groupW) / 2;
  const groupTop = pad + (availH - frontH) / 2;

  const peekW = frontW * peekScale;
  const peekH = frontH * peekScale;
  const peekTop = groupTop + (frontH - peekH) / 2;

  return {
    front: { left: groupLeft, top: groupTop, width: frontW, height: frontH },
    frontFlipped: { left: groupLeft + groupW - frontW, top: groupTop, width: frontW, height: frontH },
    peekRight: { left: groupLeft + groupW - peekW, top: peekTop, width: peekW, height: peekH },
    peekLeft: { left: groupLeft, top: peekTop, width: peekW, height: peekH },
    dotsTop: groupTop + frontH,
  };
}

/**
 * Where page `offset` steps from the current one sits: in front, peeking out
 * (the next page, or the previous one on the last page), or hidden behind
 * the peek, ready to slide in.
 */
export function pdfPagePlacement(
  layout: PdfStackLayout,
  offset: number,
  flipped: boolean,
): { box: StackBox; z: number; visible: boolean } {
  if (offset === 0) return { box: flipped ? layout.frontFlipped : layout.front, z: 3, visible: true };
  if (!flipped && offset === 1) return { box: layout.peekRight, z: 2, visible: true };
  if (flipped && offset === -1) return { box: layout.peekLeft, z: 2, visible: true };
  return { box: offset > 0 ? layout.peekRight : layout.peekLeft, z: 1, visible: false };
}

/** At most this many dots; longer documents show a sliding window of them. */
export const MAX_DOTS = 7;

/** The page indices to draw dots for, keeping `current` inside the window. */
export function dotWindow(count: number, current: number): number[] {
  if (count <= MAX_DOTS) return Array.from({ length: count }, (_, i) => i);
  const half = Math.floor(MAX_DOTS / 2);
  const start = Math.max(0, Math.min(count - MAX_DOTS, current - half));
  return Array.from({ length: MAX_DOTS }, (_, i) => start + i);
}
